import { FC, useState } from 'react';
import Table from 'components/Table';
import { Button } from '@mui/material';
import AddNewInvestorAccessGrant from './AddNewInvestorAccessGrant';
import EditInvestorAccessGrantEmails from './EditInvestorAccessGrantEmails';
import EditInvestorAccessGrantPermissions from './EditInvestorAccessGrantPermissions';
import InvestorAccessGrantActions from './InvestorAccessGrantActions';
import Heading from 'components/Heading';
import { Row } from 'components/Layout';
import Spacing from 'components/Spacing';
import trpcApi, { ApiResponses } from 'lib/trpcClient';
import Chip from 'components/Chip';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';

type Props = {
  investorId: string;
  firmId: string;
};

type UserRow = ApiResponses['investorAccessGrant']['list'][number];

const InvestorAccessGrants: FC<Props> = ({ investorId, firmId }) => {
  const [canCreate] = useHasPermissions([
    RolePermission.CreateInvestorAccessGrant,
  ]);

  const columns = [
    {
      label: 'Email address',
      key: 'email',
      sort: false,
      render: (elm: UserRow) => (
        <Spacing direction="row" size="sm">
          {elm?.email}
          {elm?.isInvestorsDefaultUser && (
            <Chip variant="success" small>
              Default
            </Chip>
          )}
        </Spacing>
      ),
    },
    {
      label: 'Access given on',
      key: 'accessGrantedDate',
      sort: false,
      render: (elm: UserRow) => elm?.accessGrantedDate?.toLabel('-') ?? '-',
    },
    {
      label: 'Copied into emails?',
      key: 'accessGrantedDate',
      sort: false,
      render: (elm: UserRow) => (
        <EditInvestorAccessGrantEmails
          copyIntoSystemEmails={elm.copyIntoSystemEmails}
          accessPermissions={elm.accessPermissions}
          furtherUserId={elm.furtherUserId}
          investorId={investorId}
          firmId={firmId}
        />
      ),
    },
    {
      label: 'Permissions',
      key: 'permissions',
      sort: false,
      render: (elm: UserRow) => (
        <EditInvestorAccessGrantPermissions
          copyIntoSystemEmails={elm.copyIntoSystemEmails}
          accessPermissions={elm.accessPermissions}
          furtherUserId={elm.furtherUserId}
          investorId={investorId}
          firmId={firmId}
        />
      ),
    },
    {
      label: 'Actions',
      key: 'actions',
      sort: false,
      render: (elm: UserRow) => (
        <InvestorAccessGrantActions
          investorId={investorId}
          furtherUserId={elm.furtherUserId}
          firmId={firmId}
          userOnlyAssociatedWithSameFirmInvestors={
            elm.userOnlyAssociatedWithSameFirmInvestors
          }
          investorHasSingleUserOnly={users?.length === 1}
          isInvestorsDefaultUser={elm.isInvestorsDefaultUser}
        />
      ),
    },
  ];

  const { data: users, isFetching } = trpcApi.investorAccessGrant.list.useQuery(
    { investorId, firmId },
  );

  const [isAddNewPanelOpen, setIsAddNewPanelOpen] = useState(false);

  const toggleAddNewAccessPanel = () => {
    setIsAddNewPanelOpen(!isAddNewPanelOpen);
  };

  const addNewAccessButton = (
    <Row justify="flex-end">
      <Button onClick={toggleAddNewAccessPanel} color="primary">
        Add new user
      </Button>
    </Row>
  );

  return (
    <Spacing>
      <Heading variant="h2">Users with access</Heading>
      <Table
        columns={columns}
        tablebody={users ?? []}
        variant="nohover"
        emptyMessage="This investor has no users with access."
        pagination={false}
        loading={isFetching}
      />
      {canCreate ? addNewAccessButton : null}
      {!isAddNewPanelOpen ? null : (
        <AddNewInvestorAccessGrant
          investorId={investorId}
          firmId={firmId}
          emailsOfExistingUsers={users?.map((user) => user.email) ?? []}
          closePanel={toggleAddNewAccessPanel}
        />
      )}
    </Spacing>
  );
};

export default InvestorAccessGrants;
